import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Input,
  Button,
  Form,
  Space,
  Typography,
  Tag,
  message,
  Row,
  Col,
  Modal,
  Drawer,
  Avatar,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CloseOutlined,
  UserOutlined,
  FilePdfOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import useCompanyReportsService from "../../services/companyReportsService";
import CompanyReport from "./CompanyReport";
import { getUserNameFromId, getUserInitials, getUserPictureFromId } from "../../config/userMapping";
import moment from "moment";

const { Title, Text } = Typography;

const CompanyReports = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const [reportDrawerVisible, setReportDrawerVisible] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [requestForm] = Form.useForm();
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [canShare, setCanShare] = useState(false);

  // Add flags to prevent duplicate calls like OrgsV2
  const isInitialMount = useRef(true);
  const initialDataLoaded = useRef(false);

  const { getTokenSilently } = useAuth0();
  const companyReportsService = useCompanyReportsService();

  const fetchReports = useCallback(async (paginationOverride) => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      
      const currentPagination = paginationOverride || pagination;
      
      const params = {
        limit: currentPagination.pageSize,
        offset: (currentPagination.current - 1) * currentPagination.pageSize,
      };

      const response = await companyReportsService.getReports(params, token);
      
      if (response.success) {
        setReports(response.reports);
        setPagination(prev => ({
          ...prev,
          total: response.hasMore ? prev.total + response.reports.length : response.reports.length,
        }));
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      message.error("Errore nel caricamento dei report");
    } finally {
      setLoading(false);
    }
  }, [getTokenSilently, companyReportsService, pagination]);

  // Initial data load only
  useEffect(() => {
    if (!initialDataLoaded.current) {
      initialDataLoaded.current = true;
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Check for Web Share API support
  useEffect(() => {
    const checkShareSupport = async () => {
      if (navigator.share && navigator.canShare) {
        try {
          // Test if we can share files
          const testFile = new File(['test'], 'test.pdf', { type: 'application/pdf' });
          const canShareFiles = await navigator.canShare({ files: [testFile] });
          setCanShare(canShareFiles);
        } catch {
          setCanShare(false);
        }
      } else {
        setCanShare(false);
      }
    };
    
    checkShareSupport();
  }, []);

  // Handle pagination changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  // Auto-refresh removed to prevent looping issues

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Cerca
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleRequestReport = async (values) => {
    try {
      setRequestLoading(true);
      const token = await getTokenSilently();
      
      const response = await companyReportsService.requestReport(values.vatNumber, token);
      
      if (response.success) {
        message.success("Richiesta report inviata con successo");
        setRequestModalVisible(false);
        requestForm.resetFields();
        fetchReports();
      }
    } catch (error) {
      console.error("Error requesting report:", error);
      message.error("Errore nella richiesta del report");
    } finally {
      setRequestLoading(false);
    }
  };

  const handleViewDetails = async (record) => {
    setSelectedReportId(record.reportId);
    setSelectedReportData(record);
    setReportDrawerVisible(true);
    
    // Fetch presignedUrl if report is completed
    if (record.status === 'COMPLETED') {
      try {
        setFetchingUrl(true);
        const token = await getTokenSilently();
        const response = await companyReportsService.getReportDetails(record.reportId, token, true);
        if (response.success && response.report?.presignedUrl) {
          setDownloadUrl(response.report.presignedUrl);
        }
      } catch (error) {
        console.error("Error fetching presigned URL:", error);
      } finally {
        setFetchingUrl(false);
      }
    }
  };

  const handleCloseReport = () => {
    setReportDrawerVisible(false);
    setSelectedReportId(null);
    setSelectedReportData(null);
    setDownloadUrl(null);
  };

  const handleDownload = async () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      message.warning("URL di download non disponibile");
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'PENDING':
        return <Tag color="processing">In elaborazione</Tag>;
      case 'COMPLETED':
        return <Tag color="success">Completato</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const validateVatNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Partita IVA richiesta'));
    }
    
    const cleanValue = value.replace(/\s/g, '');
    if (!/^\d{11}$/.test(cleanValue)) {
      return Promise.reject(new Error('La Partita IVA deve contenere 11 cifre'));
    }
    
    return Promise.resolve();
  };

  const columns = [
    {
      title: "Partita IVA",
      dataIndex: "vatNumber",
      key: "vatNumber",
      ...getColumnSearchProps("vatNumber", "Cerca Partita IVA"),
      render: (vatNumber) => (
        <Text style={{ fontFamily: 'monospace', fontSize: '13px' }}>
          {vatNumber}
        </Text>
      ),
    },
    {
      title: "Azienda",
      dataIndex: "companyName",
      key: "companyName",
      ellipsis: true,
      ...getColumnSearchProps("companyName", "Cerca Azienda"),
      render: (companyName) => (
        companyName || <Text type="secondary">-</Text>
      ),
    },
    {
      title: "Stato",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Data richiesta",
      dataIndex: "requestedAt",
      key: "requestedAt",
      render: (date) => moment(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Completato",
      dataIndex: "completedAt",
      key: "completedAt",
      render: (date) => date ? moment(date).format("DD/MM/YYYY HH:mm") : "-",
    },
    {
      title: <UserOutlined />,
      dataIndex: "requestedBy",
      key: "requestedBy",
      width: 50,
      align: "center",
      render: (requestedBy) => {
        if (!requestedBy) return "-";
        
        try {
          const displayName = getUserNameFromId(requestedBy);
          const initials = getUserInitials(requestedBy);
          const picture = getUserPictureFromId(requestedBy);
          
          return (
            <Tooltip title={displayName}>
              <Avatar 
                size="small" 
                src={picture || undefined}
                style={{ 
                  backgroundColor: '#1890ff', 
                  cursor: 'pointer',
                  color: '#fff'
                }}
              >
                {initials}
              </Avatar>
            </Tooltip>
          );
        } catch (error) {
          console.error('Error rendering user avatar:', error, requestedBy);
          // Fallback to simple display
          return (
            <Tooltip title={requestedBy}>
              <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
                {requestedBy.charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          );
        }
      },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Title level={2}>Dati Imprese</Title>
      
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchReports(pagination)}>
              Aggiorna
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setRequestModalVisible(true)}
            >
              Richiedi Report
            </Button>
          </Space>
        </Col>
      </Row>

      <Card>
        <Table
          dataSource={reports}
          columns={columns}
          rowKey="reportId"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => handleViewDetails(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Modal
        title="Richiedi nuovo report"
        open={requestModalVisible}
        onCancel={() => {
          setRequestModalVisible(false);
          requestForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form
          form={requestForm}
          layout="vertical"
          onFinish={handleRequestReport}
        >
          <Form.Item
            name="vatNumber"
            label="Partita IVA"
            rules={[{ validator: validateVatNumber }]}
          >
            <Input
              placeholder="Es: 12345678901"
              maxLength={11}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={requestLoading}
              >
                Richiedi Report
              </Button>
              <Button onClick={() => {
                setRequestModalVisible(false);
                requestForm.resetFields();
              }}>
                Annulla
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Report Completo - {selectedReportData?.companyName || selectedReportData?.vatNumber}</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={handleCloseReport}
              style={{ marginRight: -12 }}
            />
          </div>
        }
        placement="right"
        onClose={handleCloseReport}
        open={reportDrawerVisible}
        width="90%"
        closable={false}
        styles={{ body: { padding: 0 } }}
        footer={
          selectedReportData?.status === 'COMPLETED' && (
            <div style={{ textAlign: 'right', padding: '12px 24px' }}>
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                  size="large"
                  loading={fetchingUrl}
                  disabled={!downloadUrl && !fetchingUrl}
                  type="default"
                >
                  Scarica JSON
                </Button>
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  onClick={() => {
                    // Pass handlePdfDownload function to CompanyReport
                    const event = new CustomEvent('generatePDF');
                    window.dispatchEvent(event);
                  }}
                  size="large"
                >
                  Scarica PDF
                </Button>
                {canShare && (
                  <Button
                    icon={<ShareAltOutlined />}
                    onClick={() => {
                      // Pass handleShare function to CompanyReport
                      const event = new CustomEvent('sharePDF');
                      window.dispatchEvent(event);
                    }}
                    size="large"
                    type="primary"
                    style={{ 
                      background: '#52c41a',
                      borderColor: '#52c41a'
                    }}
                  >
                    Condividi
                  </Button>
                )}
              </Space>
            </div>
          )
        }
      >
        {selectedReportId && (
          <CompanyReport
            reportId={selectedReportId}
            onClose={handleCloseReport}
          />
        )}
      </Drawer>
    </div>
  );
};

export default CompanyReports;